import React, { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import ProductsPage from "./pages/ProductsPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutCompletePage from "./pages/CheckoutCompletePage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MembershipSuccessPage from "./pages/MembershipSuccessPage";
import OrdersPage from "./pages/OrdersPage";
import OrderDetailPage from "./pages/OrderDetailPage";
import AccountPage from "./pages/AccountPage";
import FoundersDashboard from "./pages/FoundersDashboard";
import DriverPortal from "./pages/DriverPortal";
import OrderingPortal from "./pages/OrderingPortal";
import ManageAccounts from "./pages/ManageAccounts";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import RefundPage from "./pages/RefundPage";

// Components
import Header from "./components/Header";
import Footer from "./components/Footer";
import AuthModal from "./components/AuthModal";
import CartSidebar from "./components/CartSidebar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Generate session ID for guest cart
const getSessionId = () => {
  let sessionId = localStorage.getItem("bundlecart_session");
  if (!sessionId) {
    sessionId = "session_" + Math.random().toString(36).substring(2, 15);
    localStorage.setItem("bundlecart_session", sessionId);
  }
  return sessionId;
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("bundlecart_token"));

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("bundlecart_token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("bundlecart_token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, fullName) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      full_name: fullName,
    });
    localStorage.setItem("bundlecart_token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("bundlecart_token");
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        console.error("Failed to refresh user:", error);
      }
    }
  };

  const upgradeMembership = async (months = 1) => {
    const response = await axios.post(
      `${API}/auth/upgrade-membership`,
      { months },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setUser((prev) => ({
      ...prev,
      is_member: true,
      membership_expires: response.data.membership_expires,
    }));
    return response.data;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        token,
        login,
        register,
        logout,
        upgradeMembership,
        refreshUser,
        isAuthenticated: !!user,
        isMember: user?.is_member || user?.subscription_status === "active" || user?.subscription_status === "cancelled" || false,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Cart Context
const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], subtotal: 0, total: 0, stores: [] });
  const [cartOpen, setCartOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [priceChanges, setPriceChanges] = useState([]);
  const { token, isMember } = useAuth();
  const sessionId = getSessionId();

  const fetchCart = async (refreshPrices = false) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = refreshPrices ? '?refresh_prices=true' : '';
      const response = await axios.get(`${API}/cart/${sessionId}${params}`, { headers });
      setCart(response.data);
      
      // Handle cart expiry
      if (response.data.cart_expired) {
        toast.info(response.data.message || "Your cart has expired");
      }
      
      // Handle price changes notification
      if (response.data.prices_refreshed && response.data.price_changes?.length > 0) {
        setPriceChanges(response.data.price_changes);
        const savings = response.data.price_changes.filter(p => p.direction === 'down');
        const increases = response.data.price_changes.filter(p => p.direction === 'up');
        
        if (savings.length > 0) {
          toast.success(`Good news! ${savings.length} item(s) dropped in price!`);
        }
        if (increases.length > 0) {
          toast.warning(`${increases.length} item(s) increased in price`);
        }
      }
      
      return response.data;
    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  };

  // Restore cart on login with price refresh
  const restoreCart = async () => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API}/cart/restore?session_id=${sessionId}`,
        {},
        { headers }
      );
      
      if (response.data.restored) {
        setCart(response.data);
        
        if (response.data.item_count > 0) {
          toast.success(`Welcome back! Your cart with ${response.data.item_count} items has been restored.`);
        }
        
        // Show price changes
        if (response.data.price_changes?.length > 0) {
          const savings = response.data.price_changes.filter(p => p.direction === 'down');
          const increases = response.data.price_changes.filter(p => p.direction === 'up');
          
          if (savings.length > 0) {
            toast.success(`Prices updated: ${savings.length} item(s) are now cheaper!`);
          }
          if (increases.length > 0) {
            toast.warning(`Note: ${increases.length} item(s) increased in price`);
          }
        }
      } else if (response.data.cart_expired) {
        toast.info(response.data.message || "Your saved cart has expired");
      }
      
      return response.data;
    } catch (error) {
      console.error("Error restoring cart:", error);
      // Fall back to regular fetch
      await fetchCart();
    }
  };

  useEffect(() => {
    if (token) {
      // On login, restore cart with price refresh
      restoreCart();
    } else {
      fetchCart();
    }
  }, [token]);

  const addToCart = async (productData) => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/cart/add-live?session_id=${sessionId}`,
        productData,
        { headers }
      );
      setCart(response.data);
      // Don't open cart sidebar - only update the badge
      toast.success("Added to cart!");
      return response.data;
    } catch (error) {
      toast.error("Failed to add to cart");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (productId, storeId, quantity) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(
        `${API}/cart/${sessionId}/item`,
        { product_id: productId, store_id: storeId, quantity },
        { headers }
      );
      await fetchCart();
    } catch (error) {
      toast.error("Failed to update cart");
      throw error;
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart/${sessionId}`);
      setCart({ items: [], subtotal: 0, total: 0, stores: [] });
    } catch (error) {
      console.error("Error clearing cart:", error);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        cartOpen,
        setCartOpen,
        addToCart,
        updateCartItem,
        clearCart,
        fetchCart,
        restoreCart,
        priceChanges,
        loading,
        sessionId,
        itemCount: cart.items?.length || 0,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Suburb Context
const SuburbContext = createContext(null);

export const useSuburb = () => useContext(SuburbContext);

const SuburbProvider = ({ children }) => {
  const [suburb, setSuburb] = useState(null);
  const [suburbData, setSuburbData] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem("bundlecart_suburb");
    if (saved) {
      const data = JSON.parse(saved);
      setSuburb(data.suburb);
      setSuburbData(data);
    }
  }, []);

  const selectSuburb = async (suburbName, postcode = null) => {
    try {
      const response = await axios.post(`${API}/suburbs/validate`, { 
        suburb: suburbName,
        postcode: postcode 
      });
      
      // Handle disambiguation case
      if (response.data.needs_disambiguation) {
        return response.data;
      }
      
      if (response.data.valid) {
        const finalData = response.data;
        setSuburb(finalData.suburb);
        setSuburbData(finalData);
        localStorage.setItem("bundlecart_suburb", JSON.stringify(finalData));
        return finalData;
      } else {
        return response.data;
      }
    } catch (error) {
      toast.error("Failed to validate suburb");
      throw error;
    }
  };

  const clearSuburb = () => {
    setSuburb(null);
    setSuburbData(null);
    localStorage.removeItem("bundlecart_suburb");
  };

  return (
    <SuburbContext.Provider
      value={{ suburb, suburbData, selectSuburb, clearSuburb }}
    >
      {children}
    </SuburbContext.Provider>
  );
};

// Main App Content
const AppContent = () => {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const { cartOpen, setCartOpen } = useCart();
  const location = useLocation();

  // Hide main header/footer for driver portal and ordering portal (they have their own headers)
  const isDriverPortal = location.pathname === "/driver";
  const isOrderingPortal = location.pathname === "/ordering-portal";
  const hideMainLayout = isDriverPortal || isOrderingPortal;

  const openAuthModal = (mode = "login") => {
    setAuthMode(mode);
    setAuthModalOpen(true);
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {!hideMainLayout && <Header openAuthModal={openAuthModal} />}
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<LandingPage openAuthModal={openAuthModal} />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/complete" element={<CheckoutCompletePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/membership/success" element={<MembershipSuccessPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/orders/:orderId" element={<OrderDetailPage />} />
          <Route path="/account" element={<AccountPage openAuthModal={openAuthModal} />} />
          <Route path="/founders" element={<FoundersDashboard />} />
          <Route path="/founders/manage-accounts" element={<ManageAccounts />} />
          <Route path="/driver" element={<DriverPortal />} />
          <Route path="/ordering-portal" element={<OrderingPortal />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/refund" element={<RefundPage />} />
        </Routes>
      </main>
      {!hideMainLayout && <Footer />}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        mode={authMode}
        setMode={setAuthMode}
      />
      <CartSidebar isOpen={cartOpen} onClose={() => setCartOpen(false)} />
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SuburbProvider>
          <CartProvider>
            <AppContent />
          </CartProvider>
        </SuburbProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
